/**
 * PDP personalisation: optional name as a line item property.
 * Blocks theme AJAX ATC when personalisation is checked without a name.
 * When checked and valid, adds the main product (with line properties)
 * plus a linked engrave variant in one /cart/add.js request.
 * Removing the main line also removes its linked engrave line.
 */
(function () {
  'use strict';

  var ATC_SELECTOR = [
    '[data-atc-form]',
    '[data-action-atc]',
    'button[name="add"]',
    '.t4s-product-form__submit',
    '.t4s-sticky-atc__atc',
  ].join(', ');

  var PROP_MAIN = '_personalisation_main';
  var PROP_ENGRAVE_VARIANT = '_personalisation_engrave_variant';

  var instances = [];
  var globalBound = false;
  var isAdding = false;
  var isRemovingLinked = false;

  /**
   * @param {HTMLElement} root
   * @returns {object|null}
   */
  function createInstance(root) {
    if (root.getAttribute('data-tw-personalisation-ready') === 'true') {
      return null;
    }

    var formId = root.getAttribute('data-form-id');
    var maxChars = parseInt(root.getAttribute('data-max-chars'), 10) || 12;
    var errorMessage = root.getAttribute('data-error-message')
      || 'Please enter a name to personalise this product.';
    var propertyName = root.getAttribute('data-property-name') || 'Personalisation';
    var engraveVariantId = parseInt(
      root.getAttribute('data-engrave-variant-id'),
      10
    ) || 0;

    var toggle = root.querySelector('[data-tw-personalisation-toggle]');
    var fields = root.querySelector('[data-tw-personalisation-fields]');
    var input = root.querySelector('[data-tw-personalisation-input]');
    var countCurrent = root.querySelector('[data-tw-personalisation-count-current]');
    var errorEl = root.querySelector('[data-tw-personalisation-error]');
    var previewValue = root.querySelector('[data-tw-personalisation-preview-value]');
    var modalValue = root.querySelector('[data-tw-personalisation-modal-value]');
    var modal = root.querySelector('[data-tw-personalisation-modal]');
    var zoomBtn = root.querySelector('[data-tw-personalisation-zoom]');
    var modalCloseBtns = root.querySelectorAll('[data-tw-personalisation-modal-close]');
    var lastFocus = null;

    if (!toggle || !fields || !input) {
      return null;
    }

    /**
     * @param {boolean} show
     * @param {string} [message]
     */
    function setError(show, message) {
      if (!errorEl) {
        return;
      }

      if (show) {
        errorEl.textContent = message || errorMessage;
        errorEl.hidden = false;
        input.classList.add('is-invalid');
        input.setAttribute('aria-invalid', 'true');
        return;
      }

      errorEl.textContent = '';
      errorEl.hidden = true;
      input.classList.remove('is-invalid');
      input.removeAttribute('aria-invalid');
    }

    function syncInputName() {
      if (toggle.checked) {
        input.disabled = false;
        input.setAttribute('name', 'properties[' + propertyName + ']');

        if (formId) {
          input.setAttribute('form', formId);
        }

        return;
      }

      input.disabled = true;
      input.removeAttribute('name');
      input.value = '';
    }

    function updatePreview() {
      var value = input.value;
      var trimmed = value.trim();

      if (countCurrent) {
        countCurrent.textContent = String(value.length);
      }

      if (previewValue) {
        previewValue.textContent = trimmed;
      }

      if (modalValue) {
        modalValue.textContent = trimmed;
      }
    }

    function openModal() {
      if (!modal) {
        return;
      }

      lastFocus = document.activeElement;
      updatePreview();
      modal.hidden = false;
      document.documentElement.classList.add('tw-personalisation-modal-open');

      var closeBtn = modal.querySelector('.tw-personalisation__modal-close');

      if (closeBtn) {
        closeBtn.focus();
      }
    }

    function closeModal() {
      if (!modal || modal.hidden) {
        return;
      }

      modal.hidden = true;
      document.documentElement.classList.remove('tw-personalisation-modal-open');

      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      } else if (zoomBtn) {
        zoomBtn.focus();
      }
    }

    function updateState() {
      fields.hidden = !toggle.checked;

      if (!toggle.checked) {
        setError(false);
        closeModal();
      }

      syncInputName();
      updatePreview();
    }

    /**
     * @returns {boolean}
     */
    function isValid() {
      if (!toggle.checked) {
        return true;
      }

      var value = input.value.trim();
      return value !== '' && value.length <= maxChars;
    }

    /**
     * @returns {boolean}
     */
    function validate() {
      if (!toggle.checked) {
        setError(false);
        syncInputName();
        return true;
      }

      var value = input.value.trim();

      if (value === '') {
        setError(true, errorMessage);
        syncInputName();
        return false;
      }

      if (value.length > maxChars) {
        setError(true, 'Maximum ' + maxChars + ' characters allowed.');
        syncInputName();
        return false;
      }

      setError(false);
      syncInputName();
      return true;
    }

    function focusInvalid() {
      fields.hidden = false;
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(function () {
        input.focus();
      }, 150);
    }

    toggle.addEventListener('change', updateState);

    input.addEventListener('input', function () {
      if (input.value.length > maxChars) {
        input.value = input.value.slice(0, maxChars);
      }

      setError(false);
      syncInputName();
      updatePreview();
    });

    if (zoomBtn) {
      zoomBtn.addEventListener('click', function (event) {
        event.preventDefault();
        openModal();
      });
    }

    Array.prototype.forEach.call(modalCloseBtns, function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        closeModal();
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeModal();
      }
    });

    root.setAttribute('data-tw-personalisation-ready', 'true');
    updateState();

    return {
      root: root,
      formId: formId,
      propertyName: propertyName,
      engraveVariantId: engraveVariantId,
      isValid: isValid,
      validate: validate,
      focusInvalid: focusInvalid,
      closeModal: closeModal,
      isPersonalised: function () {
        return toggle.checked && isValid();
      },
      getValue: function () {
        return input.value.trim();
      },
    };
  }

  /**
   * @param {HTMLElement} root
   * @returns {object|null}
   */
  function createInstanceFromReady(root) {
    var toggle = root.querySelector('[data-tw-personalisation-toggle]');
    var input = root.querySelector('[data-tw-personalisation-input]');
    var fields = root.querySelector('[data-tw-personalisation-fields]');
    var formId = root.getAttribute('data-form-id');
    var maxChars = parseInt(root.getAttribute('data-max-chars'), 10) || 12;
    var errorMessage = root.getAttribute('data-error-message')
      || 'Please enter a name to personalise this product.';
    var propertyName = root.getAttribute('data-property-name') || 'Personalisation';
    var engraveVariantId = parseInt(
      root.getAttribute('data-engrave-variant-id'),
      10
    ) || 0;
    var errorEl = root.querySelector('[data-tw-personalisation-error]');

    if (!toggle || !input || !fields) {
      return null;
    }

    return {
      root: root,
      formId: formId,
      propertyName: propertyName,
      engraveVariantId: engraveVariantId,
      isValid: function () {
        if (!toggle.checked) {
          return true;
        }

        var value = input.value.trim();
        return value !== '' && value.length <= maxChars;
      },
      validate: function () {
        if (!toggle.checked) {
          if (errorEl) {
            errorEl.hidden = true;
            errorEl.textContent = '';
          }

          input.classList.remove('is-invalid');
          return true;
        }

        var value = input.value.trim();

        if (value === '') {
          if (errorEl) {
            errorEl.textContent = errorMessage;
            errorEl.hidden = false;
          }

          input.classList.add('is-invalid');
          return false;
        }

        return true;
      },
      focusInvalid: function () {
        fields.hidden = false;
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(function () {
          input.focus();
        }, 150);
      },
      isPersonalised: function () {
        if (!toggle.checked) {
          return false;
        }

        var value = input.value.trim();
        return value !== '' && value.length <= maxChars;
      },
      getValue: function () {
        return input.value.trim();
      },
    };
  }

  /**
   * @returns {object|null}
   */
  function getInvalidInstance() {
    var i;
    var instance;

    for (i = 0; i < instances.length; i += 1) {
      instance = instances[i];

      if (!instance.isValid()) {
        instance.validate();
        return instance;
      }
    }

    return null;
  }

  /**
   * @returns {object|null}
   */
  function getPersonalisedInstance() {
    var i;
    var instance;

    for (i = 0; i < instances.length; i += 1) {
      instance = instances[i];

      if (
        instance.isPersonalised &&
        instance.isPersonalised() &&
        instance.engraveVariantId
      ) {
        return instance;
      }
    }

    return null;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {object|null}
   */
  function getRelatedInstance(form) {
    var i;
    var instance;

    for (i = 0; i < instances.length; i += 1) {
      instance = instances[i];

      if (instance.formId && form.id === instance.formId) {
        return instance;
      }
    }

    return null;
  }

  /**
   * @param {Element} button
   */
  function restoreAtcButton(button) {
    if (!button) {
      return;
    }

    if (button.getAttribute('data-tw-atc-form-restore') === 'true') {
      button.setAttribute('data-atc-form', '');
      button.removeAttribute('data-tw-atc-form-restore');
    }

    if (button.getAttribute('data-tw-action-atc-restore') === 'true') {
      button.setAttribute('data-action-atc', '');
      button.removeAttribute('data-tw-action-atc-restore');
    }
  }

  /**
   * Temporarily remove theme ATC hooks so delegated handlers do not fire.
   * @param {Element} button
   * @param {{skipRestore?: boolean}} [options]
   */
  function disarmAtcButton(button, options) {
    options = options || {};

    if (button.hasAttribute('data-atc-form')) {
      button.setAttribute('data-tw-atc-form-restore', 'true');
      button.removeAttribute('data-atc-form');
    }

    if (button.hasAttribute('data-action-atc')) {
      button.setAttribute('data-tw-action-atc-restore', 'true');
      button.removeAttribute('data-action-atc');
    }

    if (options.skipRestore) {
      return;
    }

    window.setTimeout(function () {
      if (!isAdding) {
        restoreAtcButton(button);
      }
    }, 300);
  }

  /**
   * @param {Event} event
   */
  function stopEvent(event) {
    if (event.cancelable) {
      event.preventDefault();
    }

    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  /**
   * @param {HTMLFormElement|null} form
   * @param {Element|null} button
   * @returns {number}
   */
  function resolveVariantId(form, button) {
    if (form) {
      var idField = form.querySelector('[name="id"]');

      if (idField && idField.value) {
        return parseInt(idField.value, 10) || 0;
      }
    }

    if (button && button.getAttribute('data-variant-id')) {
      return parseInt(button.getAttribute('data-variant-id'), 10) || 0;
    }

    return 0;
  }

  /**
   * @param {HTMLFormElement|null} form
   * @param {Element|null} button
   * @returns {number}
   */
  function resolveQuantity(form, button) {
    var stickyRoot = button && button.closest
      ? button.closest('[data-sticky-addtocart]')
      : null;

    if (stickyRoot) {
      var stickyQty = stickyRoot.querySelector('[data-quantity-value]');

      if (stickyQty && stickyQty.value) {
        return parseInt(stickyQty.value, 10) || 1;
      }
    }

    if (form) {
      var qtyField = form.querySelector('[name="quantity"]');

      if (qtyField && qtyField.value) {
        return parseInt(qtyField.value, 10) || 1;
      }
    }

    return 1;
  }

  /**
   * @param {HTMLFormElement|null} form
   * @returns {Object<string, string>}
   */
  function collectProperties(form) {
    var properties = {};

    if (!form) {
      return properties;
    }

    var formData = new FormData(form);

    formData.forEach(function (value, key) {
      var match = key.match(/^properties\[(.+)\]$/);

      if (match && String(value).trim() !== '') {
        properties[match[1]] = String(value);
      }
    });

    return properties;
  }

  /**
   * @param {object} instance
   * @param {Element|null} button
   * @returns {Array<object>|null}
   */
  function buildCartItems(instance, button) {
    var form = instance.formId
      ? document.getElementById(instance.formId)
      : null;
    var variantId = resolveVariantId(form, button);
    var quantity = resolveQuantity(form, button);
    var properties = collectProperties(form);
    var propertyName = instance.propertyName || 'Personalisation';
    var value = instance.getValue ? instance.getValue() : '';
    var mainItem;
    var sellingPlan;

    if (!variantId || !instance.engraveVariantId) {
      return null;
    }

    if (value && !properties[propertyName]) {
      properties[propertyName] = value;
    }

    var mainProperties = Object.assign({}, properties);
    mainProperties[PROP_MAIN] = 'true';
    mainProperties[PROP_ENGRAVE_VARIANT] = String(instance.engraveVariantId);

    mainItem = {
      id: variantId,
      quantity: quantity,
      properties: mainProperties,
    };

    if (form) {
      sellingPlan = new FormData(form).get('selling_plan');

      if (sellingPlan) {
        mainItem.selling_plan = sellingPlan;
      }
    }

    return [
      mainItem,
      {
        id: instance.engraveVariantId,
        quantity: quantity,
      },
    ];
  }

  /**
   * @returns {string}
   */
  function getShopifyRoot() {
    var root = '/';

    if (window.Shopify && Shopify.routes && Shopify.routes.root) {
      root = Shopify.routes.root;
    }

    return root.replace(/\/$/, '') + '/';
  }

  /**
   * @returns {string}
   */
  function getCartAddUrl() {
    return getShopifyRoot() + 'cart/add.js';
  }

  /**
   * @returns {string}
   */
  function getCartJsonUrl() {
    return getShopifyRoot() + 'cart.js';
  }

  /**
   * @returns {string}
   */
  function getCartChangeUrl() {
    return getShopifyRoot() + 'cart/change.js';
  }

  /**
   * @param {Element|null} button
   * @param {boolean} loading
   */
  function setButtonLoading(button, loading) {
    if (!button) {
      return;
    }

    if (loading) {
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
    } else {
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    }

    var spinner = button.querySelector('.t4s-loading__spinner');

    if (spinner) {
      spinner.hidden = !loading;
    }
  }

  /**
   * @param {object} instance
   * @param {string} message
   */
  function showAddError(instance, message) {
    var errorEl = instance.root
      ? instance.root.querySelector('[data-tw-personalisation-error]')
      : null;

    if (!errorEl) {
      return;
    }

    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  /**
   * @param {object} instance
   * @param {Element|null} button
   */
  function addPersonalisedItems(instance, button) {
    var items;

    if (isAdding) {
      return;
    }

    items = buildCartItems(instance, button);

    if (!items) {
      return;
    }

    isAdding = true;
    setButtonLoading(button, true);
    disarmAtcButton(button, { skipRestore: true });

    fetch(getCartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: items }),
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(
            function (data) {
              var message =
                (data && (data.description || data.message))
                || 'Unable to add personalisation to cart.';
              throw new Error(message);
            },
            function () {
              throw new Error('Unable to add personalisation to cart.');
            }
          );
        }

        return response.json();
      })
      .then(function () {
        document.dispatchEvent(
          new CustomEvent('cart:refresh:opend', { bubbles: true })
        );
      })
      .catch(function (error) {
        showAddError(
          instance,
          (error && error.message) || 'Unable to add personalisation to cart.'
        );
        instance.focusInvalid();
      })
      .then(function () {
        isAdding = false;
        setButtonLoading(button, false);
        restoreAtcButton(button);
      });
  }

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  function blockInvalidEvent(event) {
    var invalid = getInvalidInstance();

    if (!invalid) {
      return false;
    }

    stopEvent(event);
    invalid.focusInvalid();
    return true;
  }

  /**
   * @param {Event} event
   */
  function onAtcIntent(event) {
    var target = event.target;
    var button;
    var personalised;

    if (!target || typeof target.closest !== 'function') {
      return;
    }

    button = target.closest(ATC_SELECTOR);

    if (!button) {
      return;
    }

    if (getInvalidInstance()) {
      disarmAtcButton(button);
      blockInvalidEvent(event);
      return;
    }

    personalised = getPersonalisedInstance();

    if (!personalised) {
      return;
    }

    if (event.type === 'pointerdown' || event.type === 'touchstart') {
      disarmAtcButton(button, { skipRestore: true });
      window.setTimeout(function () {
        if (!isAdding) {
          restoreAtcButton(button);
        }
      }, 2000);
      return;
    }

    if (event.type === 'click') {
      stopEvent(event);
      addPersonalisedItems(personalised, button);
    }
  }

  /**
   * @param {Event} event
   */
  function onFormSubmit(event) {
    var form = event.target;
    var related;
    var button;

    if (!form || form.tagName !== 'FORM') {
      return;
    }

    related = getRelatedInstance(form);

    if (!related) {
      return;
    }

    if (!related.isValid()) {
      related.validate();
      stopEvent(event);
      related.focusInvalid();
      return;
    }

    if (
      related.isPersonalised &&
      related.isPersonalised() &&
      related.engraveVariantId
    ) {
      stopEvent(event);
      button = form.querySelector(ATC_SELECTOR);
      addPersonalisedItems(related, button);
    }
  }

  /**
   * @param {Event} event
   */
  function onCartRemoveIntent(event) {
    var lineKey = resolveCartLineKey(event.target);
    var cartRow = getCartItemRow(event.target);

    if (!lineKey || !cartRow || isRemovingLinked) {
      return;
    }

    if (cartRow.getAttribute('data-personalisation-role') !== 'main') {
      return;
    }

    handleLinkedCartRemoval(lineKey, cartRow, event);
  }

  /**
   * @param {Element} target
   * @returns {string|null}
   */
  function resolveCartLineKey(target) {
    var removeBtn = target.closest('[data-cart-remove]');

    if (removeBtn) {
      return removeBtn.getAttribute('data-id');
    }

    var decreaseBtn = target.closest('[data-decrease-qty]');

    if (!decreaseBtn) {
      return null;
    }

    var wrapper = decreaseBtn.closest('[data-quantity-wrapper]');
    var qtyInput = wrapper && wrapper.querySelector('[data-quantity-value]');

    if (!qtyInput) {
      return null;
    }

    var qty = parseInt(qtyInput.value, 10) || 0;
    var minQty = parseInt(qtyInput.getAttribute('min'), 10) || 1;

    if (qty > minQty) {
      return null;
    }

    return qtyInput.getAttribute('data-id');
  }

  /**
   * @param {Element} target
   * @returns {Element|null}
   */
  function getCartItemRow(target) {
    return target.closest('[data-cart-item]');
  }

  /**
   * @param {string} key
   * @param {number} quantity
   * @returns {Promise<object>}
   */
  function changeCartLineQuantity(key, quantity) {
    return fetch(getCartChangeUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: key,
        quantity: quantity,
      }),
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(
          function (data) {
            var message =
              (data && (data.description || data.message))
              || 'Unable to update cart.';
            throw new Error(message);
          },
          function () {
            throw new Error('Unable to update cart.');
          }
        );
      }

      return response.json();
    });
  }

  /**
   * @param {Array<{key: string, quantity: number}>} updates
   * @returns {Promise<void>}
   */
  function applyCartUpdates(updates) {
    var chain = Promise.resolve();
    var i;

    for (i = 0; i < updates.length; i += 1) {
      chain = chain.then(function (update) {
        return function () {
          return changeCartLineQuantity(update.key, update.quantity);
        };
      }(updates[i]));
    }

    return chain;
  }

  /**
   * @param {object} item
   * @param {number} engraveVariantId
   * @returns {boolean}
   */
  function isEngraveLine(item, engraveVariantId) {
    if (!item || !engraveVariantId) {
      return false;
    }

    return parseInt(item.id, 10) === engraveVariantId
      || parseInt(item.variant_id, 10) === engraveVariantId;
  }

  /**
   * @param {string} lineKey
   * @param {Element} cartRow
   * @param {Event} event
   */
  function handleLinkedCartRemoval(lineKey, cartRow, event) {
    var engraveVariantAttr = cartRow.getAttribute(
      'data-personalisation-engrave-variant'
    );

    stopEvent(event);
    isRemovingLinked = true;

    fetch(getCartJsonUrl())
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var updates = [{ key: lineKey, quantity: 0 }];
        var i;
        var item;
        var mainItem = null;
        var engraveVariantId = parseInt(engraveVariantAttr, 10) || 0;
        var remainingQty;

        for (i = 0; i < cart.items.length; i += 1) {
          if (cart.items[i].key === lineKey) {
            mainItem = cart.items[i];
            break;
          }
        }

        if (
          mainItem &&
          mainItem.properties &&
          mainItem.properties[PROP_ENGRAVE_VARIANT]
        ) {
          engraveVariantId = parseInt(
            mainItem.properties[PROP_ENGRAVE_VARIANT],
            10
          ) || engraveVariantId;
        }

        if (!engraveVariantId) {
          return applyCartUpdates(updates);
        }

        for (i = 0; i < cart.items.length; i += 1) {
          item = cart.items[i];

          if (item.key === lineKey || !isEngraveLine(item, engraveVariantId)) {
            continue;
          }

          remainingQty = item.quantity - (mainItem ? mainItem.quantity : 1);
          updates.push({
            key: item.key,
            quantity: remainingQty > 0 ? remainingQty : 0,
          });
          break;
        }

        return applyCartUpdates(updates);
      })
      .then(function () {
        document.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true })
        );
      })
      .catch(function () {
        document.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true })
        );
      })
      .then(function () {
        isRemovingLinked = false;
      });
  }

  function bindGlobalHandlers() {
    if (globalBound) {
      return;
    }

    globalBound = true;

    // Capture phase must run before theme jQuery delegated ATC handlers
    document.addEventListener('pointerdown', onAtcIntent, true);
    document.addEventListener('touchstart', onAtcIntent, true);
    document.addEventListener('click', onAtcIntent, true);
    document.addEventListener('click', onCartRemoveIntent, true);
    document.addEventListener('submit', onFormSubmit, true);
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-tw-personalisation]');
    var i;
    var instance;

    instances = [];

    for (i = 0; i < roots.length; i += 1) {
      instance = createInstance(roots[i]);

      if (instance) {
        instances.push(instance);
      } else if (roots[i].getAttribute('data-tw-personalisation-ready') === 'true') {
        instance = createInstanceFromReady(roots[i]);

        if (instance) {
          instances.push(instance);
        }
      }
    }

    bindGlobalHandlers();
  }

  function boot() {
    initAll();
    document.addEventListener('shopify:section:load', initAll);
    document.addEventListener('shopify:block:select', initAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
